import { useEffect } from 'react';
import { useMapController } from '../map/MapShell.js';
import { useThemeStore } from '../../app/themeStore.js';
import { useGotoStore } from './gotoStore.js';
import { usePickPointStore } from './pickPointStore.js';
import { startGotoMetrics, stopGotoMetrics } from './gotoMetrics.js';
import { GotoChip } from './GotoChip.js';
import { PickPointSheet } from './PickPointSheet.js';
import { msg } from '../../lib/messages.js';
import './goto.css';

/**
 * Mounted unconditionally on the Naviger screen (spec 13, same shape as
 * TrailLayer/PredictorLayer): draws the go-to pin + line, wires long-press
 * and the "Velg i kartet" pick-mode tap, runs the chip's live metrics, and
 * renders the confirm sheet + chip. Nothing here needs a route or the
 * network — the whole feature works fully offline for a tapped point.
 */
export function GotoLayer(): JSX.Element | null {
  const controller = useMapController();
  const target = useGotoStore((s) => s.target);
  const theme = useThemeStore((s) => s.theme); // re-tint on day/night
  const pickModeActive = usePickPointStore((s) => s.pickModeActive);
  const cancelPickMode = usePickPointStore((s) => s.cancelPickMode);
  const proposePoint = usePickPointStore((s) => s.proposePoint);

  useEffect(() => {
    startGotoMetrics();
    return () => stopGotoMetrics();
  }, []);

  useEffect(() => {
    if (!controller) return;
    controller.setGoto(target?.point ?? null);
    return () => controller.clearGoto();
  }, [controller, target, theme]);

  useEffect(() => {
    if (!controller) return;
    return controller.onLongPress((lat, lon) => proposePoint(lat, lon));
  }, [controller, proposePoint]);

  useEffect(() => {
    if (!controller || !pickModeActive) return undefined;
    return controller.onMapClick((lat, lon) => proposePoint(lat, lon));
  }, [controller, pickModeActive, proposePoint]);

  return (
    <>
      {pickModeActive && (
        <div className="goto-pick-overlay">
          <div className="goto-pick-overlay__status">
            {msg.goto.pickHint}
            <button type="button" className="goto-pick-overlay__cancel" onClick={cancelPickMode}>
              {msg.goto.cancel}
            </button>
          </div>
        </div>
      )}
      <PickPointSheet />
      <GotoChip />
    </>
  );
}
